import { LayoutMode, LeftTab } from "@/store/zustand/layoutStore";

// Constants moved from component
export const DEFAULT_LEFT_PANEL_SIZE = 19;
export const LEFT_PANEL_AI_CHAT_SIZE = 22;
export const DEFAULT_LEFT_PANEL_EXTENSION_SIZE = 35;
export const LEFT_EXTENSION_PRD_SIZE = 33;
export const LEFT_EXTENSION_ENG_SPEC_SIZE = 33;
export const DEFAULT_RIGHT_SIZE = 0;
export const RIGHT_OPEN_SIZE = 17;

// Min width constants
export const COMMUNITY_TAB_MIN_WIDTH = 520;
export const PROJECT_SETTINGS_MIN_WIDTH = 400;
export const ASSETS_PANEL_MIN_WIDTH = 380;
// show 2 columns of assets panel & don't squeeze chat
export const LEFT_PANEL_AI_CHAT_MIN_WIDTH = 350;
export const LEFT_PANEL_AI_CHAT_MAX_WIDTH = 600;

export interface PanelSizes {
  leftPanelSize: number;
  leftPanelExtensionSize: number;
  rightPanelSize: number;
  canvasSize: number;
}

export interface PanelState {
  leftExpanded: boolean;
  rightExpanded: boolean;
  layoutMode: LayoutMode;
  leftTab: LeftTab;
  leftPanelSize: number;
  leftPanelExtensionSize: number;
  rightPanelSize: number;
}

/**
 * Calculate minimum size percentage for left panel based on tab and screen width
 */
export function getLeftPanelMinSize(
  leftTab: LeftTab,
  screenWidth: number,
): number {
  let minWidthPx: number;
  if (leftTab === LeftTab.Community) {
    minWidthPx = COMMUNITY_TAB_MIN_WIDTH;
  } else if (leftTab === LeftTab.ProjectSettings) {
    minWidthPx = PROJECT_SETTINGS_MIN_WIDTH;
  } else if (leftTab === LeftTab.Assets) {
    minWidthPx = ASSETS_PANEL_MIN_WIDTH;
  } else {
    minWidthPx = LEFT_PANEL_AI_CHAT_MIN_WIDTH;
  }
  return Math.max(5, (minWidthPx / screenWidth) * 100);
}

/**
 * Calculate maximum size percentage for left panel based on tab and screen width
 */
export function getLeftPanelMaxSize(
  leftTab: LeftTab,
  screenWidth: number,
): number {
  let maxWidthPx: number;
  if (leftTab === LeftTab.AIChat || leftTab === LeftTab.EngSpec) {
    maxWidthPx = LEFT_PANEL_AI_CHAT_MAX_WIDTH;
  } else {
    // For other tabs, allow up to 50% of screen width as max
    maxWidthPx = screenWidth * 0.5;
  }
  return Math.min(50, (maxWidthPx / screenWidth) * 100);
}

/**
 * Check if left extension panel should be visible
 */
export function isLeftExtensionVisible(
  leftExpanded: boolean,
  layoutMode: LayoutMode,
  leftTab: LeftTab,
): boolean {
  return (
    leftExpanded &&
    layoutMode !== LayoutMode.Design &&
    leftTab !== LeftTab.SupabaseConnect &&
    leftTab !== LeftTab.LocalEditing &&
    leftTab !== LeftTab.Info
  );
}

/**
 * Check if right panel should be visible
 */
export function isRightPanelVisible(
  rightExpanded: boolean,
  layoutMode: LayoutMode,
  isFullScreenEnabled?: boolean,
): boolean {
  return (
    rightExpanded && layoutMode === LayoutMode.Design && !isFullScreenEnabled
  );
}

/**
 * Calculate panel sizes based on current layout state
 */
export function calculatePanelSizes(state: PanelState): PanelSizes {
  const {
    leftExpanded,
    rightExpanded,
    layoutMode,
    leftTab,
    leftPanelSize,
    leftPanelExtensionSize,
    rightPanelSize,
  } = state;

  const leftExtensionVisible = isLeftExtensionVisible(
    leftExpanded,
    layoutMode,
    leftTab,
  );
  const rightPanelVisible = isRightPanelVisible(rightExpanded, layoutMode);

  const layoutArray: number[] = [];

  // Left panel calculation
  let newLeftPanelSize: number;
  if (!leftExpanded) {
    newLeftPanelSize = 0;
  } else {
    let defaultCalculatedSize: number;
    if (
      leftTab === LeftTab.AIChat ||
      leftTab === LeftTab.EngSpec ||
      leftTab === LeftTab.SupabaseConnect ||
      leftTab === LeftTab.LocalEditing ||
      leftTab === LeftTab.Info ||
      leftTab === LeftTab.ProjectSettings
    ) {
      defaultCalculatedSize = LEFT_PANEL_AI_CHAT_SIZE;
    } else {
      defaultCalculatedSize = DEFAULT_LEFT_PANEL_SIZE;
    }
    newLeftPanelSize =
      leftPanelSize > 0 ? leftPanelSize : defaultCalculatedSize;
  }
  layoutArray.push(newLeftPanelSize);

  // Left panel extension calculation
  let newLeftPanelExtensionSize: number;
  if (!leftExtensionVisible) {
    newLeftPanelExtensionSize = 0;
  } else {
    let defaultCalculatedSize: number;
    if (layoutMode === LayoutMode.PRD) {
      defaultCalculatedSize = LEFT_EXTENSION_PRD_SIZE;
    } else if (layoutMode === LayoutMode.Code) {
      defaultCalculatedSize = LEFT_EXTENSION_ENG_SPEC_SIZE;
    } else {
      defaultCalculatedSize = DEFAULT_LEFT_PANEL_EXTENSION_SIZE;
    }
    newLeftPanelExtensionSize =
      leftPanelExtensionSize > 0
        ? leftPanelExtensionSize
        : defaultCalculatedSize;
  }
  layoutArray.push(newLeftPanelExtensionSize);

  // Right panel calculation
  let newRightPanelSize: number;
  if (!rightPanelVisible) {
    newRightPanelSize = DEFAULT_RIGHT_SIZE; // 0
  } else {
    newRightPanelSize = rightPanelSize > 0 ? rightPanelSize : RIGHT_OPEN_SIZE;
  }
  layoutArray.push(newRightPanelSize);

  // Canvas panel - calculated as remaining space
  const sumOfOtherPanels = layoutArray.reduce((a, b) => a + b, 0);
  const canvasSize = Math.max(0, 100 - sumOfOtherPanels);
  layoutArray.push(canvasSize);

  return {
    leftPanelSize: layoutArray[0],
    leftPanelExtensionSize: layoutArray[1],
    rightPanelSize: layoutArray[2],
    canvasSize: layoutArray[3],
  };
}

/**
 * Calculate new percentage sizes to maintain target pixel widths during window resize
 */
export function calculateResizePercentages(
  targetPixelWidths: {
    left: number;
    leftExt: number;
    right: number;
  },
  newContainerWidth: number,
  minSizes: {
    leftMinSize: number;
    rightMinSize: number;
  },
  visibility: {
    leftExpanded: boolean;
    leftExtensionVisible: boolean;
    rightPanelVisible: boolean;
  },
): {
  leftPanelSize: number;
  leftExtensionSize: number;
  rightPanelSize: number;
} {
  const { leftMinSize, rightMinSize } = minSizes;
  const { leftExpanded, leftExtensionVisible, rightPanelVisible } = visibility;

  const newLeftPanelSize =
    leftExpanded && targetPixelWidths.left > 0
      ? Math.max(
          leftMinSize,
          Math.round((targetPixelWidths.left / newContainerWidth) * 100 * 100) /
            100,
        )
      : 0;

  const newLeftExtSize =
    leftExtensionVisible && targetPixelWidths.leftExt > 0
      ? Math.round(
          (targetPixelWidths.leftExt / newContainerWidth) * 100 * 100,
        ) / 100
      : 0;

  const newRightSize =
    rightPanelVisible && targetPixelWidths.right > 0
      ? Math.max(
          rightMinSize,
          Math.round(
            (targetPixelWidths.right / newContainerWidth) * 100 * 100,
          ) / 100,
        )
      : 0;

  return {
    leftPanelSize: newLeftPanelSize,
    leftExtensionSize: newLeftExtSize,
    rightPanelSize: newRightSize,
  };
}

/**
 * Convert percentages to pixel widths
 */
export function percentagesToPixelWidths(
  percentages: {
    leftPanelSize: number;
    leftPanelExtensionSize: number;
    rightPanelSize: number;
  },
  containerWidth: number,
): {
  left: number;
  leftExt: number;
  right: number;
} {
  return {
    left: Math.round((percentages.leftPanelSize / 100) * containerWidth),
    leftExt: Math.round(
      (percentages.leftPanelExtensionSize / 100) * containerWidth,
    ),
    right: Math.round((percentages.rightPanelSize / 100) * containerWidth),
  };
}
